import { Link } from '@tanstack/react-router';
import { useAreaConfig, useSearchSubjects } from '@/hooks';
import { ADMIN_SCROLLBAR_CLASSES } from '../constants';
import { ColoredBadge } from '../components';

interface SubjectsListProps {
  areaSlug: string;
}

const TYPE_BADGE: Record<string, { label: string; color: string }> = {
  '3D':    { label: '3D',    color: '#C3D82E' },
  'Image': { label: 'Image', color: '#60A5FA' },
  '360':   { label: '360°',  color: '#FB923C' },
};

const SUBJECTS_ONLY = {
  resultTypesFilter: [{ id: 'subject' as const, name: 'Subject' }],
  unitsFilter: [],
  categoryGroupFilters: [],
  disciplinesFilter: [],
  modelTypesFilter: [],
};

export const SubjectsList = ({ areaSlug }: SubjectsListProps) => {
  const { data: areaConfig } = useAreaConfig(areaSlug, 'EDITOR');
  const { data: results, isLoading, isError } = useSearchSubjects(
    areaSlug,
    500,
    1,
    undefined,
    SUBJECTS_ONLY
  );

  const singularName = areaConfig?.subjectName.singular ?? 'Subject';
  const pluralName   = areaConfig?.subjectName.plural   ?? 'Subjects';

  const subjects = results?.items ?? [];

  return (
    <div className="px-4 py-6 max-w-4xl space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#E8E8E8] capitalize">{pluralName}</h1>
          {!isLoading && results && (
            <p className="text-sm text-[#999] mt-1">
              {results.totalSubjects} {results.totalSubjects === 1 ? singularName.toLowerCase() : pluralName.toLowerCase()} in this area
            </p>
          )}
        </div>
        <Link
          to="/admin/areas/$areaSlug/subjects/new"
          params={{ areaSlug }}
          className="px-4 py-2 text-sm rounded bg-[#C3D82E] text-[#1A1A1A] font-semibold hover:bg-[#D4E845] transition-colors"
        >
          + Add {singularName}
        </Link>
      </div>

      {/* Body */}
      {isLoading && (
        <div className="text-sm text-[#999]">Loading {pluralName.toLowerCase()}…</div>
      )}

      {isError && (
        <div className="text-sm text-red-400">Failed to load {pluralName.toLowerCase()}.</div>
      )}

      {!isLoading && !isError && subjects.length === 0 && (
        <div className="text-sm text-[#999]">
          No {pluralName.toLowerCase()} yet.{' '}
          <Link
            to="/admin/areas/$areaSlug/subjects/new"
            params={{ areaSlug }}
            className="text-[#C3D82E] underline"
          >
            Add the first one.
          </Link>
        </div>
      )}

      {subjects.length > 0 && (
        <div className={`overflow-y-auto max-h-[calc(100vh-200px)] ${ADMIN_SCROLLBAR_CLASSES}`}>
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_80px_64px] gap-4 px-4 pb-2">
            <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">Name</span>
            <span className="text-xs font-semibold text-[#999] uppercase tracking-wide">Type</span>
            <span />
          </div>

          {/* Rows */}
          {subjects.map((subject) => {
            const badge = TYPE_BADGE[subject.type] ?? { label: subject.type, color: '#888' };
            return (
              <div
                key={subject.subjectSlug}
                className="grid grid-cols-[1fr_80px_64px] gap-4 px-4 py-3 rounded hover:bg-[#303030] transition-colors items-center"
              >
                {/* Name + description */}
                <div className="min-w-0">
                  <span className="text-sm font-medium text-[#E8E8E8] truncate block">
                    {subject.subjectName}
                  </span>
                  {subject.subjectDescription && (
                    <span className="text-xs text-[#999] truncate block mt-0.5">
                      {subject.subjectDescription}
                    </span>
                  )}
                </div>

                {/* Type badge */}
                <ColoredBadge color={badge.color} bold>{badge.label}</ColoredBadge>

                {/* Edit link */}
                <Link
                  to="/admin/areas/$areaSlug/subjects/$subjectSlug"
                  params={{ areaSlug, subjectSlug: subject.subjectSlug }}
                  className="text-xs text-[#C3D82E] hover:underline text-right"
                >
                  Edit →
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
